import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  serverTimestamp,
  Timestamp,
  deleteField,
  writeBatch,
  onSnapshot,
  limit,
  orderBy,
  type DocumentData,
  type Query
} from "firebase/firestore";
import { db, functions } from "./firebase";
import { Booking, Closure, PricingSettings, User, LaneTimeSlot } from "../types";
// Csak TÍPUS: a `guestDetails.ts` futásidejű behúzása a telefon-metaadatot (~50 kB gzip)
// is beemelné a kezdeti csomagba, mert az `AuthContext` ezt a modult eagerly importálja
import type { StoredGuestDetails } from "../guestDetails";
import type { PersonName } from "../personName";
// A számlák szótára is CSAK TÍPUS, ugyanabból az okból: az `AuthContext` ezt a modult
// eagerly importálja, tehát egy futásidejű `invoicing/invoices` import az egész számlázó
// szótárat (és vele az `amounts.ts`-t) beemelné a NYITÓOLDAL csomagjába - egy olyan képernyőre,
// ahol számla nincs is. A `draftToInvoice`-t ezért a `subscribeToInvoiceSources` tölti be
// menet közben (`import()`), amikor az admin tényleg megnyitja a Számlák szekciót.
import type {
  Invoice,
  InvoiceDraft,
  InvoiceIssuer,
  InvoiceSyncStatus,
  SavedInvoiceItem,
  SavedInvoicePartner,
} from "../invoicing/types";
import { httpsCallable } from "firebase/functions";

// Árak lekérése
// Alapértelmezett árak, amíg az admin nem mentett beállítást
export const DEFAULT_PRICING_SETTINGS: PricingSettings = {
  monday: {
    isOpen: true,
    openingHour: 10,
    closingHour: 23,
    hourlyRates: {
      10: 4500, 11: 4500, 12: 4500, 13: 4500, 14: 4500, 15: 4500, 16: 4500, 17: 4500, 18: 4500,
      19: 5000, 20: 5000, 21: 5000, 22: 5000
    },
    minGuestsPerLane: 1,
    maxGuestsPerLane: 6
  },
  tuesday: {
    isOpen: true,
    openingHour: 10,
    closingHour: 23,
    hourlyRates: {
      10: 4500, 11: 4500, 12: 4500, 13: 4500, 14: 4500, 15: 4500, 16: 4500, 17: 4500, 18: 4500,
      19: 5000, 20: 5000, 21: 5000, 22: 5000
    },
    minGuestsPerLane: 1,
    maxGuestsPerLane: 6
  },
  wednesday: {
    isOpen: true,
    openingHour: 10,
    closingHour: 23,
    hourlyRates: {
      10: 4500, 11: 4500, 12: 4500, 13: 4500, 14: 4500, 15: 4500, 16: 4500, 17: 4500, 18: 4500,
      19: 5000, 20: 5000, 21: 5000, 22: 5000
    },
    minGuestsPerLane: 1,
    maxGuestsPerLane: 6
  },
  thursday: {
    isOpen: true,
    openingHour: 10,
    closingHour: 23,
    hourlyRates: {
      10: 4500, 11: 4500, 12: 4500, 13: 4500, 14: 4500, 15: 4500, 16: 4500, 17: 4500, 18: 4500,
      19: 5000, 20: 5000, 21: 5000, 22: 5000
    },
    minGuestsPerLane: 1,
    maxGuestsPerLane: 6
  },
  friday: {
    isOpen: true,
    openingHour: 10,
    closingHour: 25, // 01:00
    hourlyRates: {
      10: 4500, 11: 4500, 12: 4500, 13: 4500, 14: 4500, 15: 4500, 16: 4500, 17: 4500, 18: 4500,
      19: 6500, 20: 6500, 21: 6500, 22: 6500, 23: 6500, 24: 6500, 0: 6500
    },
    minGuestsPerLane: 1,
    maxGuestsPerLane: 6
  },
  saturday: {
    isOpen: true,
    openingHour: 10,
    closingHour: 25, // 01:00
    hourlyRates: {
      10: 5000, 11: 5000, 12: 5000, 13: 5000, 14: 5000, 15: 5000, 16: 5000, 17: 5000, 18: 5000,
      19: 6500, 20: 6500, 21: 6500, 22: 6500, 23: 6500, 24: 6500, 0: 6500
    },
    minGuestsPerLane: 1,
    maxGuestsPerLane: 6
  },
  sunday: {
    isOpen: true,
    openingHour: 10,
    closingHour: 23,
    hourlyRates: {
      10: 5000, 11: 5000, 12: 5000, 13: 5000, 14: 5000, 15: 5000, 16: 5000, 17: 5000, 18: 5000,
      19: 5000, 20: 5000, 21: 5000, 22: 5000
    },
    minGuestsPerLane: 1,
    maxGuestsPerLane: 6
  },
  shoesRentalPrice: 200
};

export const getPricingSettings = async (): Promise<PricingSettings> => {
  const pricingRef = doc(db, "settings", "pricing");
  const pricingDoc = await getDoc(pricingRef);
  if (pricingDoc.exists()) {
    return pricingDoc.data() as PricingSettings;
  }
  // Mentsük el az alapértelmezett árakat
  await setDoc(pricingRef, DEFAULT_PRICING_SETTINGS);
  return DEFAULT_PRICING_SETTINGS;
};

// Árak valós idejű figyelése. A lokális cache miatt (lásd firebase.ts) visszatéréskor az első
// értéket azonnal, a cache-ből kapja a hívó; a szerver csak a változást küldi. Képernyőre
// szánt adathoz ezt használd, ne a getPricingSettings-t - az mindig a szerverre vár.
export const subscribeToPricingSettings = (
  onChange: (pricing: PricingSettings) => void,
  onError: (error: Error) => void,
): (() => void) =>
  onSnapshot(
    doc(db, "settings", "pricing"),
    // A metaadat-változás is kell: a "nincs dokumentum" cache- és szerver-válasza között csak a
    // metaadat tér el, enélkül a szerver megerősítése sosem érkezne meg (lásd lentebb)
    { includeMetadataChanges: true },
    (snapshot) => {
      if (snapshot.exists()) {
        onChange(snapshot.data() as PricingSettings);
        return;
      }
      // Cache-ből jövő "nincs dokumentum" csak annyit jelent: offline vagyunk, és ez a gép még
      // sosem töltötte le az árakat. Ilyenkor nem mutathatjuk az alapértékeket valódiként - a
      // figyelő él tovább, és amint van hálózat, a szerver értéke jön. Alapérték csak akkor,
      // ha a szerver mondja, hogy tényleg nincs mentett beállítás.
      if (snapshot.metadata.fromCache) {
        onError(new Error("Az árak offline nem érhetők el"));
        return;
      }
      onChange(DEFAULT_PRICING_SETTINGS);
    },
    onError,
  );

// Árak frissítése
export const updatePricingSettings = async (pricing: PricingSettings): Promise<void> => {
  const docRef = doc(db, "settings", "pricing");
  await setDoc(docRef, pricing);
};

// ---------------------------------------------------------------------------
// Kivételek (zárások és rendkívüli kinyitások)
//
// A kivételek az árak dokumentumának `closures` tömbjében élnek, tehát minden
// módosítás olvasás-módosítás-írás. Ez a két függvény az egyetlen hely, ahol ez a
// kör le van írva: a képernyő a valós idejű figyelőből (`usePricingService`) olvas,
// az írás viszont a MENTETT állapotból indul, hogy egy közben mentett másik kivétel
// ne vesszen el. A tömeges törlés is egyetlen írás - nem elemenkénti kör.
// ---------------------------------------------------------------------------

/** Egy kivétel mentése: azonos azonosítón csere, egyébként hozzáfűzés. */
export const saveClosure = async (closure: Closure): Promise<void> => {
  const pricing = await getPricingSettings();
  const closures = pricing.closures ?? [];
  const exists = closures.some((entry) => entry.id === closure.id);

  await updatePricingSettings({
    ...pricing,
    closures: exists
      ? closures.map((entry) => (entry.id === closure.id ? closure : entry))
      : [...closures, closure],
  });
};

/** Kivételek törlése azonosító szerint - egy vagy több, egyetlen írásban. */
export const deleteClosures = async (ids: readonly string[]): Promise<void> => {
  const pricing = await getPricingSettings();
  const removed = new Set(ids);

  await updatePricingSettings({
    ...pricing,
    closures: (pricing.closures ?? []).filter((closure) => !removed.has(closure.id)),
  });
};

/**
 * Foglalás létrehozása. EZT A FÜGGVÉNYT az admin szerkesztő hívja (EditBookingForm); a
 * publikus űrlap közvetlenül a callable-t hívja (BookingForm), mert ott a zárolás-token is
 * megy vele.
 *
 * Itt régen állt egy készpénz-ellenőrzés (`users/{userId}.allowCashPayment`), de rossz
 * helyen: `userId` nélkül és hiányzó felhasználó-dokumentumnál némán átengedett, a publikus
 * űrlap pedig amúgy sem jött erre. Vagyis egyedül az ADMIN-t akasztotta meg, amikor
 * helyszínen fizetett foglalást rögzített olyan vendégnek, akinek nincs engedélye - pont
 * azt, aki maga a jogosultság.
 *
 * A kapu innentől a szerveren van (`createBooking` -> `lib/cashEligibility.js`), ahol nem
 * kerülhető meg, és ahol a BEJELENTKEZETT AZONOSÍTÓ dönt - nem a kérésben küldött e-mail
 * cím, amit bárki beírhat. A foglalási űrlap ugyanezt a mezőt olvassa a saját profil-
 * figyelőjéből, de csak azért, hogy felkínálja-e a lehetőséget.
 */
export const createBooking = async (booking: Omit<Booking, "id" | "createdAt" | "updatedAt" | "userId">, userId: string | null): Promise<string> => {
    // Előkészítjük a foglalás adatait
    const bookingData = {
      ...booking,
      userId: userId || "guest",
    };
    


    // Cloud Function meghívása a foglalás létrehozásához
    // A Cloud Function tranzakciót használ, ami megakadályozza a versenyhelyzeteket (race condition)
    const createBookingFn = httpsCallable(functions, 'createBooking');
    const result = await createBookingFn(bookingData);
    const data = result.data as { id: string };
    return data.id;
};

// Foglalás frissítése
export const updateBooking = async (id: string, booking: Partial<Booking>): Promise<void> => {
  const docRef = doc(db, "bookings", id);
  await updateDoc(docRef, {
    ...booking,
    updatedAt: serverTimestamp(),
    adminModified: true, // Jelöljük, hogy admin módosította
    modificationEmailSent: false // Reset email flag
  });
};

// A soft delete mezői - egy és több foglalás törlése is pontosan ezt írja
const softDeleteFields = (userId: string) => ({
  deletedAt: serverTimestamp(),
  deletedBy: userId,
  updatedAt: serverTimestamp(),
});

// Foglalás törlése (soft delete)
export const deleteBooking = async (id: string, userId: string): Promise<void> => {
  await updateDoc(doc(db, "bookings", id), softDeleteFields(userId));
};

const DELETE_BATCH_SIZE = 400;

/**
 * Több foglalás törlése kötegelve.
 *
 * Foglalásonként egy `updateDoc` a tömeges törlésnél nem járható út: az „összes kijelölése”
 * a teljes szűrt listára megy, így egyetlen kattintásból több ezer párhuzamos írás lenne.
 * Egy `writeBatch` legfeljebb 500 műveletet vihet, és kötegen belül atomi - vagy mind a 400
 * foglalás törlődik, vagy egy sem -, a kötegeket pedig egymás után küldjük.
 *
 * A hívó azt kapja vissza, mi sikerült: egy megszakadt köteg nem teszi tönkre a többit, és a
 * bennmaradt azonosítókra újra lehet próbálni. Ezért nem dob, hanem beszámol.
 */
export const deleteBookings = async (
  ids: readonly string[],
  userId: string,
): Promise<{ deletedIds: string[]; failedIds: string[] }> => {
  const deletedIds: string[] = [];
  const failedIds: string[] = [];

  for (let start = 0; start < ids.length; start += DELETE_BATCH_SIZE) {
    const chunk = ids.slice(start, start + DELETE_BATCH_SIZE);
    const batch = writeBatch(db);
    chunk.forEach((id) => batch.update(doc(db, "bookings", id), softDeleteFields(userId)));

    try {
      await batch.commit();
      deletedIds.push(...chunk);
    } catch (error) {
      console.error("Hiba a foglalások kötegelt törlése közben:", error);
      failedIds.push(...chunk);
    }
  }

  return { deletedIds, failedIds };
};

// Foglalás visszaállítása (restore from soft delete)
export const restoreBooking = async (id: string): Promise<void> => {
  const docRef = doc(db, "bookings", id);
  await updateDoc(docRef, {
    deletedAt: deleteField(),
    deletedBy: deleteField(),
    updatedAt: serverTimestamp()
  });
};

// ---------------------------------------------------------------------------
// Ideiglenes zárolások Booking nézete
// ---------------------------------------------------------------------------

const toIsoString = (value: unknown): string | undefined => {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "string") return value;
  return undefined;
};

// Fizetési zárolás: a kliens temp_ előtagú azonosítóval, a Teya rendelésazonosítóval és
// külön tulajdonos-hash-sel hozza létre; minden más zárolás kijelölés a rácson
const isPaymentLock = (id: string, data: DocumentData): boolean =>
  id.startsWith("temp_") ||
  typeof data.paymentLockOwnerHash === "string" ||
  typeof data.tempOrderId === "string";

// A temporary_bookings dokumentum Booking alakban: a foglaltsági rács, az admin lista és
// a pályanézet is ezt használja. Az isTemporary/lockKind alapján a UI külön jelvényt ad
// neki, és nem kínál rá szerkesztést/törlést (nem foglalás, hanem élő zárolás).
const temporaryBookingToBooking = (id: string, data: DocumentData): Booking => ({
  id,
  userId: data.userId || "temp_user",
  name: data.name || "Ideiglenes foglalás",
  email: data.email || "",
  phone: data.phone || "",
  ...(data.secondaryPhone ? { secondaryPhone: data.secondaryPhone as string } : {}),
  date: data.date,
  startTime: data.startTime,
  endTime: data.endTime,
  laneCount: data.lanes?.length || 0,
  guestCount: data.guestCount || 0,
  shoesRental: data.shoesRental || 0,
  totalPrice: data.totalPrice || 0,
  paymentMethod: data.paymentMethod || "teya",
  paymentStatus: "pending",
  bookingStatus: "active", // a zárolás foglalja a pályát, ezért aktívként kezeljük
  createdAt: toIsoString(data.createdAt) || new Date().toISOString(),
  updatedAt: toIsoString(data.updatedAt) || new Date().toISOString(),
  lanes: data.lanes || [],
  // Pontos cellák, ha a zárolás így készült - a rács is ezt használja
  ...(Array.isArray(data.laneTimeSlots) && data.laneTimeSlots.length > 0
    ? { laneTimeSlots: data.laneTimeSlots }
    : {}),
  ...(typeof data.lockOwnerHash === "string" ? { lockOwnerHash: data.lockOwnerHash } : {}),
  ...(typeof data.paymentLockOwnerHash === "string"
    ? { paymentLockOwnerHash: data.paymentLockOwnerHash }
    : {}),
  ...(typeof data.tempOrderId === "string" ? { tempOrderId: data.tempOrderId } : {}),
  ...(typeof data.shortOrderId === "string" ? { shortOrderId: data.shortOrderId } : {}),
  notes: data.notes,
  isTemporary: true,
  lockKind: isPaymentLock(id, data) ? "payment" : "selection",
  lockExpiresAt: toIsoString(data.expiresAt),
});

// A lejárt zárolást a takarító függvény 5 percenként törli; addig a kliens szűri ki
const isLiveLock = (booking: Booking, now: number): boolean =>
  !booking.lockExpiresAt || new Date(booking.lockExpiresAt).getTime() > now;

// Foglalások lekérése dátum alapján
export const getBookingsByDate = async (date: string): Promise<Booking[]> => {
  try {
    // Végleges foglalások lekérése
    const bookingsQuery = query(collection(db, "bookings"), where("date", "==", date));
    const bookingsSnapshot = await getDocs(bookingsQuery);

    // Ideiglenes zárolások lekérése (csak az érvényesek)
    const tempBookingsQuery = query(
      collection(db, "temporary_bookings"),
      where("date", "==", date),
      where("expiresAt", ">", Timestamp.now())
    );
    const tempBookingsSnapshot = await getDocs(tempBookingsQuery);

    const bookings = bookingsSnapshot.docs
      .map(doc => convertTimestampFields(doc.data(), doc.id))
      .filter(booking => booking.bookingStatus !== "cancelled" && !booking.deletedAt);

    const tempBookings = tempBookingsSnapshot.docs.map(doc =>
      temporaryBookingToBooking(doc.id, doc.data())
    );

    return [...bookings, ...tempBookings]
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  } catch {
    return []; // Hiba esetén üres tömböt adunk vissza
  }
};

// Felhasználó keresése email alapján
export const findUserByEmail = async (email: string): Promise<User | null> => {
  const usersCollection = collection(db, "users");
  const q = query(usersCollection, where("email", "==", email));
  
  const querySnapshot = await getDocs(q);
  if (querySnapshot.empty) {
    return null;
  }
  
  return querySnapshot.docs[0].data() as User;
};

// Foglalás lezárása és email küldése
export const completeBooking = async (id: string): Promise<void> => {
  const docRef = doc(db, "bookings", id);
  const bookingDoc = await getDoc(docRef);
  
  if (!bookingDoc.exists()) {
    throw new Error("A foglalás nem található");
  }
  
  const bookingData = bookingDoc.data() as Booking;
  
  // Ellenőrizzük, hogy a foglalás már teljesített állapotban van-e
  if (bookingData.bookingStatus === "completed") {
    // Csak az updatedAt mezőt frissítjük, nem küldünk új e-mailt
    await updateDoc(docRef, {
      updatedAt: serverTimestamp()
    });
    return;
  }
  
  // Foglalás állapotának frissítése
  // Az email küldést a Firestore trigger fogja végezni (onBookingCompleted)
  await updateDoc(docRef, {
    bookingStatus: "completed",
    paymentStatus: "paid", // Biztosítjuk, hogy a fizetés is "paid" legyen
    updatedAt: serverTimestamp()
  });
};

// Helper függvény a Timestamp mezők konvertálásához
const convertTimestampFields = (data: Record<string, unknown>, docId: string): Booking => {
  const formattedData: Record<string, unknown> = {
    ...data,
    id: docId,
  };
  
  // Timestamp mezők konvertálása ISO string-re
  const timestampFields = ['createdAt', 'updatedAt', 'deletedAt', 'submissionDate'];
  timestampFields.forEach(field => {
    const value = data[field];
    if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
      formattedData[field] = value.toDate().toISOString();
    }
  });
  
  return formattedData as unknown as Booking;
};

/**
 * EGY foglalás valós időben - a számla-szerkesztő „foglalásból" útja ebből tölt.
 *
 * Nem a teljes admin-figyelőből keressük ki: a szerkesztő saját, teljes képernyős útvonal
 * az admin kereten KÍVÜL, ott nincs `useAdminOutlet`, és egy közvetlen link (vagy egy
 * frissítés) a foglalások listája nélkül is ide érkezhet. `null`, ha a foglalás nem létezik.
 *
 * A dokumentum-azonosító egyetlen útvonal-szegmens lehet: egy '/'-t tartalmazó (vagy üres)
 * azonosítóra a `doc()` SZINKRON hibát dobna, ami a feliratkozó effektet - és vele az oldalt -
 * vinné el. Ezért az `onError`-ra tesszük: a hívó a figyelő hibájaként kapja meg, nem
 * összeomlásként.
 *
 * A lágyan törölt foglalás (`deletedAt`) is megjön - hogy abból készülhet-e számla, azt a
 * hívó dönti el, nem a figyelő.
 */
export const subscribeToBooking = (
  bookingId: string,
  onChange: (booking: Booking | null) => void,
  onError: (error: Error) => void,
): (() => void) => {
  if (!bookingId || bookingId.includes("/")) {
    onError(new Error(`Érvénytelen foglalás-azonosító: ${bookingId}`));
    return () => {};
  }

  try {
    return onSnapshot(
      doc(db, "bookings", bookingId),
      (snapshot) =>
        onChange(snapshot.exists() ? convertTimestampFields(snapshot.data(), snapshot.id) : null),
      onError,
    );
  } catch (error) {
    onError(error instanceof Error ? error : new Error(String(error)));
    return () => {};
  }
};

export interface BookingsSubscriptionOptions {
  includeDeleted?: boolean;
}

interface BookingSourcesOptions {
  /** A végleges foglalások lekérdezése */
  confirmed: Query<DocumentData>;
  /** A zárolások lekérdezése - a lejártakat a `expiresAt` feltétel mellett is szűrjük */
  temporary: Query<DocumentData>;
  /** Bekerül-e a végleges foglalás a listába (az admin a töröltet is kérheti) */
  keepConfirmed: (booking: Booking) => boolean;
  /**
   * A zárolások nélkül megbízható-e a lista. A szerkesztő rácsánál NEM: ott a hiányzó zárolás
   * szabad cellának látszik, a foglalás frissítése pedig nyers kliensírás, szerveroldali
   * ütközés-vizsgálat nélkül, tehát egy fizetés alatt álló pályára is rámenthetne. Ott a
   * zárolások hibája ugyanolyan tartós, mint a foglalásoké (`true`), és a szerkesztő nem ment.
   * Az áttekintő admin listák nem írnak a zárolások alapján: nekik a foglalások teljesek, a
   * hiba ott csak a konzolba kerül (`false`), egy „foglalások betöltése” toast félrevezetne.
   */
  locksAreCritical: boolean;
}

// Valós idejű foglaláslista: a végleges foglalások és az élő zárolások együtt, Firestore
// onSnapshot alapján. Az első hívás csak akkor történik, ha mindkét forrás megérkezett;
// utána minden változás (új foglalás, fizetés véglegesítése, zárolás törlése) azonnal
// lefut. Az admin lista és a foglalási rács ugyanezt a két forrást olvassa, csak más
// szűréssel - ezért a két figyelő itt, egy helyen él.
const subscribeToBookingSources = (
  {
    confirmed: confirmedQuery,
    temporary: temporaryQuery,
    keepConfirmed,
    locksAreCritical,
  }: BookingSourcesOptions,
  onChange: (bookings: Booking[]) => void,
  onError: (error: Error) => void,
): (() => void) => {
  let confirmed: Booking[] | null = null;
  let temporary: Booking[] | null = null;
  // Egy Firestore figyelő hiba után véglegesen leáll. Ilyenkor a másik forrás adatait
  // akkor is kiadjuk (különben a lista tartósan üresen maradna, pedig az ép figyelő
  // továbbra is szállít). A tartós hibát minden adag UTÁN újra kimondjuk (a `useSubscription`
  // szerződése: az adat törli a hibát), így a nézet hibaállapota megmarad:
  //  - a FOGLALÁSOKÉ mindig tartós: nélkülük a lista üres vagy elavult, a rács szabadnak
  //    mutatná a foglalt órákat, a Teendők azt mondaná, nincs teendő;
  //  - a ZÁROLÁSOKÉ csak ott, ahol a lista nélkülük nem megbízható (`locksAreCritical`: a
  //    szerkesztő rácsa); máshol csak a konzolba kerül.
  let confirmedError: Error | null = null;
  let temporaryError: Error | null = null;
  let lastLiveLockIds = "";

  const liveLocks = () => (temporary ?? []).filter(booking => isLiveLock(booking, Date.now()));

  /** Kiadja a listát, ha mindkét forrás megszólalt; `false`, ha még nem. */
  const emit = (): boolean => {
    const confirmedReady = confirmed !== null || confirmedError !== null;
    const temporaryReady = temporary !== null || temporaryError !== null;
    if (!confirmedReady || !temporaryReady) return false;

    const live = liveLocks();
    lastLiveLockIds = live.map(booking => booking.id).join(",");
    onChange([...(confirmed ?? []), ...live]);
    // Az adag UTÁN: az `onChange` a `useSubscription`-ben törli a hibát, és enélkül a
    // foglalások (vagy a szerkesztőnél a zárolások) nélküli lista teljesnek látszana
    const failure = confirmedError ?? (locksAreCritical ? temporaryError : null);
    if (failure) onError(failure);
    return true;
  };

  const handleError = (source: "bookings" | "temporary") => (error: Error) => {
    if (source === "bookings") {
      confirmedError = error;
      // Ha a lista még nem mehet ki (a zárolások hallgatnak), a hibát magában mondjuk ki
      if (!emit()) onError(error);
      return;
    }
    temporaryError = error;
    if (locksAreCritical) {
      // Ha a lista még nem mehet ki (a foglalások hallgatnak), a hibát magában mondjuk ki
      if (!emit()) onError(error);
      return;
    }
    console.error("A zárolások figyelése leállt, a lista zárolások nélkül megy tovább:", error);
    emit();
  };

  // A lekérdezés "most" feltétele a feliratkozáskor rögzül, ezért a lejáró zárolásokat
  // magunknak kell kiszűrnünk. Csak akkor adunk új listát, ha tényleg lejárt valami -
  // különben percenként feleslegesen újrarajzolnánk az egész admin nézetet.
  const dropExpiredLocks = () => {
    if (temporary === null) return;
    if (liveLocks().map(booking => booking.id).join(",") === lastLiveLockIds) return;
    emit();
  };

  const unsubscribeBookings = onSnapshot(
    confirmedQuery,
    (snapshot) => {
      confirmed = snapshot.docs
        .map(doc => convertTimestampFields(doc.data(), doc.id))
        .filter(keepConfirmed);
      confirmedError = null;
      emit();
    },
    handleError("bookings"),
  );

  const unsubscribeTemporary = onSnapshot(
    temporaryQuery,
    (snapshot) => {
      temporary = snapshot.docs.map(doc => temporaryBookingToBooking(doc.id, doc.data()));
      temporaryError = null;
      emit();
    },
    handleError("temporary"),
  );

  const expiryTimer = setInterval(dropExpiredLocks, 60 * 1000);

  return () => {
    clearInterval(expiryTimer);
    unsubscribeBookings();
    unsubscribeTemporary();
  };
};

/** Minden foglalás és élő zárolás - az admin listák, a pályanézet és az analitika forrása. */
export const subscribeToBookingsWithTemporary = (
  onChange: (bookings: Booking[]) => void,
  onError: (error: Error) => void,
  { includeDeleted = false }: BookingsSubscriptionOptions = {},
): (() => void) =>
  subscribeToBookingSources(
    {
      confirmed: query(collection(db, "bookings")),
      temporary: query(collection(db, "temporary_bookings"), where("expiresAt", ">", Timestamp.now())),
      keepConfirmed: booking => includeDeleted || !booking.deletedAt,
      locksAreCritical: false,
    },
    onChange,
    onError,
  );

/**
 * Egy nap foglaltsága a nyilvános foglalási rácsnak („yyyy-MM-dd”).
 *
 * A lemondott foglalás nem foglaltság, ezért itt - az admin listával ellentétben - kimarad.
 * A rács ezt a figyelőt kapja, nem egyszeri lekérést: amíg a vendég a cellákat nézi, más
 * foglalása és zárolása magától megjelenik rajta.
 */
export const subscribeToBookingsByDate = (
  date: string,
  onChange: (bookings: Booking[]) => void,
  onError: (error: Error) => void,
): (() => void) =>
  subscribeToBookingSources(
    {
      confirmed: query(collection(db, "bookings"), where("date", "==", date)),
      temporary: query(
        collection(db, "temporary_bookings"),
        where("date", "==", date),
        where("expiresAt", ">", Timestamp.now()),
      ),
      keepConfirmed: booking => booking.bookingStatus !== "cancelled" && !booking.deletedAt,
      locksAreCritical: true,
    },
    onChange,
    onError,
  );

// ---------------------------------------------------------------------------
// A nyilvános foglaltsági tükör: availability/{yyyy-MM-dd}
//
// A rácsnak csak annyit kell tudnia, mely pálya-óra cellák foglaltak - a
// `bookings`/`temporary_bookings` dokumentumokban viszont a vendég NEVE, e-mail címe,
// telefonszáma, megjegyzése és számlázási adatai is ott vannak, és ma ezek is kimennek
// minden böngészőbe. A Firestore szabályai nem tudnak mezőt szűrni, ezért a rács alá egy
// külön, személyes adat nélküli napi dokumentum került (a szerver írja:
// `functions/src/lib/availability.js`). Terv: `docs/public-booking-reads.md`.
//
// Ez a függvény a tükröt visszafordítja abba az alakba, amit a rács amúgy is vár: cellánként
// EGY `Booking`, a saját `laneTimeSlots`-ával. Így a `buildLaneGrid` /
// `isLaneTimeSlotBooked` fölötte változatlan marad, és a képernyőn semmi nem mozdul.
// ---------------------------------------------------------------------------

/** Egy cella a tükörben. Csak ennyi van benne - azonosító, név, összeg nincs. */
interface AvailabilityCell {
  kind?: "booking" | "lock";
  /** Zárolásnál a tulajdonos token-hash-e: ettől ismeri fel a vendég a SAJÁT kijelölését */
  ownerHash?: string;
  /** Zárolásnál a lejárat (ISO) - a lejárás nem írás, tehát a tükröt nem számolná újra senki */
  expiresAt?: string;
}

const AVAILABILITY_CELL_KEY = /^(\d{1,2})@(\d{1,2}:\d{2})$/;

/**
 * Egy cella `Booking` alakban. Minden mezője igaz: a szintetikus foglalás pontosan egy
 * pályát és egy órát fed, ezért a rács ugyanazt a foglaltságot számolja belőle, mint ma a
 * teljes dokumentumokból. Azonosító nincs benne (a tükörben sincs), a kulcs a cella maga.
 */
const availabilityCellToBooking = (
  date: string,
  key: string,
  cell: AvailabilityCell,
  updatedAt: string,
): Booking | null => {
  const match = AVAILABILITY_CELL_KEY.exec(key);
  if (!match) return null;

  const lane = Number(match[1]);
  const startTime = match[2];
  const hour = parseInt(startTime, 10);
  if (!Number.isFinite(lane) || !Number.isFinite(hour)) return null;

  const endTime = `${String((hour + 1) % 24).padStart(2, "0")}:00`;

  return {
    id: `availability:${key}`,
    userId: "",
    name: "Foglalt",
    email: "",
    phone: "",
    date,
    startTime,
    endTime,
    laneCount: 1,
    guestCount: 0,
    shoesRental: 0,
    totalPrice: 0,
    paymentMethod: "teya",
    paymentStatus: "pending",
    bookingStatus: "active",
    createdAt: updatedAt,
    updatedAt,
    lanes: [lane],
    laneTimeSlots: [{ lane, startTime, endTime }],
    ...(cell.ownerHash ? { lockOwnerHash: cell.ownerHash } : {}),
    ...(cell.kind === "lock"
      ? { isTemporary: true, ...(cell.expiresAt ? { lockExpiresAt: cell.expiresAt } : {}) }
      : {}),
  };
};

/**
 * Egy nap foglaltsága a nyilvános rácsnak, a tükörből („yyyy-MM-dd”).
 *
 * Figyelő, nem egyszeri lekérés - EGYETLEN dokumentumé: a valós idejűség megmarad (más
 * vendég kijelölése továbbra is magától megjelenik a rácson), a letöltött adat viszont a
 * nap összes foglalásáról egyetlen kis dokumentumra zsugorodik.
 *
 * A lejárt zárolást ugyanúgy magunk ejtjük ki, ahogy a `subscribeToBookingSources` teszi:
 * a lejárat nem írás, tehát a szerver nem számolná újra miatta a napot.
 *
 * Hiányzó dokumentum = üres nap. Ez azt is jelenti, hogy egy fel nem töltött (backfill
 * nélküli) nap szabadnak látszana - ezért kell a kliens átállítása ELŐTT a backfill.
 */
export const subscribeToDayAvailability = (
  date: string,
  onChange: (bookings: Booking[]) => void,
  onError: (error: Error) => void,
): (() => void) => {
  let cells: Booking[] | null = null;
  let lastLiveIds = "";

  const liveCells = () => (cells ?? []).filter(cell => isLiveLock(cell, Date.now()));

  const emit = () => {
    if (cells === null) return;
    const live = liveCells();
    lastLiveIds = live.map(cell => cell.id).join(",");
    onChange(live);
  };

  const dropExpiredLocks = () => {
    if (cells === null) return;
    if (liveCells().map(cell => cell.id).join(",") === lastLiveIds) return;
    emit();
  };

  const unsubscribe = onSnapshot(
    doc(db, "availability", date),
    (snapshot) => {
      const data = snapshot.data();
      const updatedAt = toIsoString(data?.updatedAt) || new Date().toISOString();
      const rawCells = (data?.cells ?? {}) as Record<string, AvailabilityCell>;
      cells = Object.entries(rawCells)
        .map(([key, cell]) => availabilityCellToBooking(date, key, cell ?? {}, updatedAt))
        .filter((booking): booking is Booking => booking !== null);
      emit();
    },
    onError,
  );

  const expiryTimer = setInterval(dropExpiredLocks, 60 * 1000);

  return () => {
    clearInterval(expiryTimer);
    unsubscribe();
  };
};

/**
 * A bejelentkezett vendég egy SAJÁT, élő zárolása egy napon: a tulajdonos-hash-ei és a cellái.
 * A cellákra bontás a rácsé (`useDayAvailability`, a `laneGrid.ts` függvényeivel), itt csak a
 * nyers mezők vannak.
 */
export interface AccountLock {
  id: string;
  /** A zárolás token-hash-ei (kijelölési és/vagy fizetési) */
  hashes: string[];
  lanes?: number[];
  startTime?: string;
  endTime?: string;
  laneTimeSlots?: LaneTimeSlot[];
}

/**
 * A bejelentkezett vendég SAJÁT, élő zárolásai egy napon, bármelyik böngészőből.
 *
 * A tükörben a zárolás csak egy token-hash-t hordoz, és a böngésző a saját tokenjéből csak a
 * SAJÁT böngészőjében indított zárolást ismeri fel. Aki a telefonján félbehagyta a fizetést,
 * majd a gépén bejelentkezve újrakezdte, a saját időpontját „Foglalt”-nak látta volna, a
 * szerver viszont a fiók szerint is sajátnak veszi (`functions/src/lib/lockOwner.js`). Ez a
 * figyelő hozza a fiókhoz tartozó zárolásokat, hogy a rács ugyanazt lássa.
 *
 * A rács CELLÁRA pontosan illeszt, nem csak hash szerint, és csak ÉLŐ zárolással: egy közös
 * gépen (pult, család) ugyanaz a böngésző, tehát ugyanaz a token-hash egy később, bejelentkezés
 * nélkül foglaló idegené is lehet. A szerver az ő zárolását nem veszi a vendégének (a fiók szerint
 * csak a `userId` köt össze), a rács tehát sem mutathatja szabadnak. A lejárt (vagy elengedett,
 * de a késő Teya-visszahívásnak megőrzött) zárolást ezért kihagyjuk, és a következő lejáratkor
 * magunktól újra küldünk, hogy a render tiszta maradjon (nincs benne `Date.now()`).
 *
 * Csak a látott napra olvas (`userId` + `date`, két egyenlőség, összetett index nélkül), mint a
 * szerver `collectOwnedLocks`-a. A `userId`-t a szerver írja a hitelesítésből, tehát nem
 * hamisítható; a `docs/public-booking-reads.md` 5. lépésének szabálya ezt a lekérdezést engedi.
 */
export const subscribeToAccountLocks = (
  uid: string,
  date: string,
  onChange: (locks: AccountLock[]) => void,
  onError: (error: Error) => void,
): (() => void) => {
  let entries: { lock: AccountLock; expiresAt: number | null }[] = [];
  let lastSignature: string | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const emit = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    const now = Date.now();
    const live = entries.filter((entry) => entry.expiresAt === null || entry.expiresAt > now);
    const nextExpiry = Math.min(
      ...live.map((entry) => entry.expiresAt ?? Number.POSITIVE_INFINITY),
    );
    // Legfeljebb óránként: a `setTimeout` 2^31 ms fölött azonnal lefutna, és a régi szerver még
    // tetszőleges (akár évekkel későbbi) kliens-lejáratot is elfogadott
    if (Number.isFinite(nextExpiry)) {
      timer = setTimeout(emit, Math.min(nextExpiry - now + 50, 60 * 60 * 1000));
    }
    // Minden kattintás a rácson egy új kijelölési zárolást ír: csak akkor adunk tovább, ha
    // tényleg más a lista, különben a rács fölöslegesen újrarajzolódna
    const signature = live.map((entry) => `${entry.lock.id}#${entry.expiresAt ?? ""}`).join(",");
    if (signature === lastSignature) return;
    lastSignature = signature;
    onChange(live.map((entry) => entry.lock));
  };

  const unsubscribe = onSnapshot(
    query(
      collection(db, "temporary_bookings"),
      where("userId", "==", uid),
      where("date", "==", date),
    ),
    (snapshot) => {
      entries = snapshot.docs
        .map((lockDoc) => {
          const data = lockDoc.data();
          const hashes = [data.lockOwnerHash, data.paymentLockOwnerHash].filter(
            (hash): hash is string => typeof hash === "string" && hash.length > 0,
          );
          const expiresAtIso = toIsoString(data.expiresAt);
          return {
            lock: {
              id: lockDoc.id,
              hashes,
              ...(Array.isArray(data.lanes) ? { lanes: data.lanes as number[] } : {}),
              ...(typeof data.startTime === "string" ? { startTime: data.startTime } : {}),
              ...(typeof data.endTime === "string" ? { endTime: data.endTime } : {}),
              ...(Array.isArray(data.laneTimeSlots)
                ? { laneTimeSlots: data.laneTimeSlots as LaneTimeSlot[] }
                : {}),
            },
            expiresAt: expiresAtIso ? new Date(expiresAtIso).getTime() : null,
          };
        })
        .filter((entry) => entry.lock.hashes.length > 0)
        .sort((a, b) => a.lock.id.localeCompare(b.lock.id));
      emit();
    },
    onError,
  );

  return () => {
    if (timer) clearTimeout(timer);
    unsubscribe();
  };
};

/**
 * A foglalás lekérése rendelésazonosító alapján - a szerverről.
 *
 * A jogosultság maga az azonosító: kitalálhatatlan, és csak az kapja meg, aki a fizetést
 * indította. A callable a megjelenítéshez szükséges mezőket adja vissza (a céges és
 * számlázási adatokat nem), `null`-t pedig akkor, ha a foglalás még nem jött létre.
 */
const getBookingByOrderId = async (orderId: string): Promise<Booking | null> => {
  const getBookingByOrderIdFn = httpsCallable<{ orderId: string }, { booking: Booking | null }>(
    functions,
    "getBookingByOrderId",
  );
  const result = await getBookingByOrderIdFn({ orderId });
  return result.data?.booking ?? null;
};

/**
 * Ezekre a lekérdezés végleg leáll, mert újrapróbálva sem lesz más a válasz: hibás azonosító,
 * vagy a callable nem érhető el ennek a kliensnek (nincs telepítve, jogosultság). Minden más
 * (hálózat, hidegindítás, `unavailable`, `internal`) átmeneti, a következő kör újrapróbálja.
 */
const PERMANENT_ORDER_LOOKUP_ERRORS = new Set([
  "functions/invalid-argument",
  "functions/not-found",
  "functions/permission-denied",
  "functions/unauthenticated",
  "functions/failed-precondition",
  "functions/unimplemented",
]);

/**
 * Ha a hibák sorozata ennyi ideig tart, és legalább `ORDER_LOOKUP_STALLED_MIN_FAILURES` kérdés
 * bukott el benne, a lekérdezés ELAKADT: jelezzük a hívónak, de NEM adjuk fel, lassan tovább
 * kérdezünk. Kódja alapján egy kapcsolatkiesés (liftben, alagútban) és egy nem telepített
 * függvény sem választható el (az SDK mindkettőt `internal`-ként adja), és a kapcsolat
 * visszatérhet: a visszaigazolás akkor is jelenjen meg, ne egy végleges „nem tudjuk”. A sorozat
 * az első ÉSZLELT hibánál kezdődik, és egy válasz lezárja: egy háttérbe tett, percekig alvó lap
 * felébredve elbukó első kérdése így nem akad el.
 */
const ORDER_LOOKUP_STALLED_AFTER_MS = 60 * 1000;
const ORDER_LOOKUP_STALLED_MIN_FAILURES = 5;

/** A lekérdezés hibája: `permanent` esetén a kérdezgetés leállt, különben tovább fut. */
export interface OrderLookupError {
  error: Error;
  permanent: boolean;
}

/** 2 mp-enként kérdezünk az első percben, utána 5 mp-enként. */
const ORDER_POLL_FAST_MS = 2000;
const ORDER_POLL_SLOW_MS = 5000;
const ORDER_POLL_FAST_WINDOW_MS = 60 * 1000;

/**
 * A fizetés után létrejövő foglalás követése a rendelésazonosító alapján.
 *
 * Itt SZÁNDÉKOSAN nem figyelő, hanem lekérdezés ismétlődik - a projekt egyetlen ilyen
 * képernyője. A vendégfoglaláshoz nincs fiók, tehát a `bookings` nyilvános olvasásának
 * megszüntetése után (`docs/public-booking-reads.md`) nincs mire feliratkozni; a foglalás
 * viszont másodperceken belül megszületik, addig pedig sűrűn kérdezünk. A hívó ugyanazt a
 * szerződést kapja, mint eddig: `null`, amíg nincs meg, aztán a foglalás.
 *
 * Az azonosító lehet a fizetési zárolásé (`temp_...`), a Teya rövid azonosítója (`t...`), vagy
 * egy régi folyamatban maga a foglalás dokumentum-azonosítója; hogy melyik, azt a callable dönti
 * el. Minden választ továbbad, a „még nincs meg” `null`-t is: a hívó abból tudja, hogy a
 * lekérdezés FRISSEN nem találta. Az `onError` kétféle: `permanent` (hibás azonosító, vagy a
 * callable nem érhető el ennek a kliensnek: `PERMANENT_ORDER_LOOKUP_ERRORS`), ekkor a
 * kérdezgetés leáll; vagy elakadás (legalább öt hiba egy percen át:
 * `ORDER_LOOKUP_STALLED_AFTER_MS`), ekkor lassan tovább kérdez, és a következő válasz ugyanúgy
 * megérkezik. A régi, közvetlen Firestore-figyelő tartalékát a
 * nyilvános olvasás szűkítése (2026-09-27) után töröltük: a szabály már mindig elutasította
 * volna, és egy első körös hálózati hiba így végleg megakasztotta a visszaigazolást.
 */
export const subscribeToBookingByOrderId = (
  orderId: string,
  onChange: (booking: Booking | null) => void,
  onError: (failure: OrderLookupError) => void,
): (() => void) => {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const startedAt = Date.now();
  // Az éppen tartó hibasorozat: mikor észleltük az elsőt, hány hiba volt benne, és jeleztük-e
  let failingSince: number | null = null;
  let failures = 0;
  let stallReported = false;

  const report = (error: unknown, permanent: boolean) => {
    if (stopped) return;
    onError({
      error: error instanceof Error ? error : new Error("A foglalás nem kérdezhető le"),
      permanent,
    });
  };

  const poll = async () => {
    if (stopped) return;
    let keepPolling = true;

    try {
      const booking = await getBookingByOrderId(orderId);
      failingSince = null;
      failures = 0;
      stallReported = false;
      if (!stopped) onChange(booking);
    } catch (error) {
      const code = (error as { code?: string }).code ?? "";
      failures += 1;
      failingSince ??= Date.now();
      const stalled =
        failures >= ORDER_LOOKUP_STALLED_MIN_FAILURES &&
        Date.now() - failingSince >= ORDER_LOOKUP_STALLED_AFTER_MS;
      if (PERMANENT_ORDER_LOOKUP_ERRORS.has(code)) {
        keepPolling = false;
        report(error, true);
      } else {
        // Átmeneti hiba (hálózat, hidegindítás): a következő kör újrapróbálja. A naplóba és a
        // hívónak is sorozatonként egyszer szólunk: egy offline telefon 5 mp-enként kérdez
        if (failures === 1) console.warn("A foglalás lekérdezése nem sikerült, újrapróbáljuk:", error);
        if (stalled && !stallReported) {
          stallReported = true;
          report(error, false);
        }
      }
    }

    if (stopped || !keepPolling) return;
    const elapsed = Date.now() - startedAt;
    timer = setTimeout(poll, elapsed < ORDER_POLL_FAST_WINDOW_MS ? ORDER_POLL_FAST_MS : ORDER_POLL_SLOW_MS);
  };

  void poll();

  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
  };
};

// Admin felhasználók száma
// A users kollekció valós időben (admin ügyféllista). A dokumentum azonosítója az uid, ha
// a mező hiányzik.
export const subscribeToUsers = (
  onChange: (users: User[]) => void,
  onError: (error: Error) => void,
): (() => void) =>
  onSnapshot(
    collection(db, "users"),
    (snapshot) =>
      onChange(
        snapshot.docs.map((docSnap) => {
          const data = docSnap.data() as User;
          return { ...data, uid: data.uid || docSnap.id };
        }),
      ),
    onError,
  );

// Admin jogosultság: az adminUsers/{uid} dokumentum léte (a Firestore-szabályok is ezt nézik).
// Hiba esetén (hálózat, jogosultság) nem admin - a felület ilyenkor a vendégoldalakat mutatja.
export const isAdminUser = async (uid: string): Promise<boolean> => {
  try {
    const adminDoc = await getDoc(doc(db, "adminUsers", uid));
    return adminDoc.exists();
  } catch (error) {
    console.error("Hiba az admin jogosultság ellenőrzésekor:", error);
    return false;
  }
};

export interface GrantAdminAccessResult {
  uid: string;
  /** A cím kisbetűs, körbevágott alakban - ezzel jött létre / ezt találtuk meg. */
  email: string;
  /** Új Auth-fiók készült-e, vagy egy meglévő kapta meg a jogot. */
  created: boolean;
  /** Már admin volt: a jogosultságán ez a hívás nem változtatott. */
  alreadyAdmin: boolean;
  /** Az admin adott jelszót. Ha nem, a vendégnek e-mailben kell beállítania. */
  passwordSet: boolean;
}

/**
 * „Ez a személy legyen admin."
 *
 * A TELJES műveletet a szerver végzi (`functions/src/admins.js`): megkeresi vagy létrehozza
 * az Auth-fiókot, beírja az `adminUsers/{uid}` dokumentumot (ez a jogosultság forrása), és
 * ráigazítja a `users/{uid}.isAdmin` tükröt; a custom claimet az `adminUsers` triggere írja.
 *
 * Kliensről ez nem végezhető el: a `createUserWithEmailAndPassword` az ÉPPEN BEJELENTKEZETT
 * munkamenetet írná át az új fiókra (az admin kilépne a sajátjából), és az utána következő
 * jogosultsági írás már egy jogosulatlan felhasználó nevében futna. E-mail cím alapján uid-t
 * keresni pedig a kliens SDK egyáltalán nem tud. Az `adminUsers` kollekciót a
 * `firestore.rules` ezért `allow write: if false`-ra zárja.
 *
 * KÉT alakja van, mert kétféleképpen lehet megnevezni valakit:
 *   - `uid`: egy MEGLÉVŐ fiók - a Felhasználók lista admin-kapcsolója hívja így, ahol a sor
 *     már egy létező fiókot nevez meg (fiók ezen az ágon nem keletkezhet),
 *   - `email`: egy SZEMÉLY, akinek lehet, hogy még nincs fiókja - az admin-meghívó űrlap.
 *
 * A hiba `HttpsError`-ként jön, magyar üzenettel - a hívó azt írja ki (lásd `lib/teya.ts`).
 */
/**
 * VAGY egy meglévő fiók azonosítója, VAGY egy e-mail cím - a kettő együtt nem értelmes,
 * ezért unió, nem két elhagyható mező. A `never` ágak miatt a jelszó a uid-es alakon már
 * fordításkor hiba (a szerver is elutasítaná: azzal más fiókját lehetne átvenni).
 */
export type GrantAdminAccessParams =
  | { uid: string; email?: never; password?: never; displayName?: string }
  | { email: string; uid?: never; displayName?: string; password?: string };

export const grantAdminAccess = async (
  params: GrantAdminAccessParams,
): Promise<GrantAdminAccessResult> => {
  const grantAdminAccessFn = httpsCallable<GrantAdminAccessParams, GrantAdminAccessResult>(
    functions,
    "grantAdminAccess",
  );
  const result = await grantAdminAccessFn(params);
  return result.data;
};

export interface RevokeAdminAccessResult {
  uid: string;
  /** Az `adminUsers` dokumentumon tárolt cím, ha volt - naplózáshoz és visszajelzéshez. */
  email: string | null;
  /** Volt-e egyáltalán admin. `false` = nem történt semmi; ez nem hiba. */
  wasAdmin: boolean;
}

/**
 * „Ez a személy NE legyen admin." - a `grantAdminAccess` párja, ugyanazzal az indoklással.
 *
 * A szerver egyetlen tranzakcióban törli az `adminUsers/{uid}` dokumentumot és állítja
 * hamisra a `users/{uid}.isAdmin` tükröt, tehát nincs olyan pillanat, amikor a lista
 * jelvénye és a tényleges jog mást mond. Korábban ez a kliensről ment, két külön írással.
 *
 * Az utolsó admin jogát a szerver nem engedi elvenni (`failed-precondition`): üres
 * `adminUsers` kollekcióval az admin felület elérhetetlenné válna, és csak a Firebase
 * konzolból lehetne visszaállítani.
 */
export const revokeAdminAccess = async (params: {
  uid: string;
}): Promise<RevokeAdminAccessResult> => {
  const revokeAdminAccessFn = httpsCallable<typeof params, RevokeAdminAccessResult>(
    functions,
    "revokeAdminAccess",
  );
  const result = await revokeAdminAccessFn(params);
  return result.data;
};

export interface DeleteUserAccountResult {
  uid: string;
  /** Volt-e Auth-fiók, amit most töröltünk (`false`: már nem létezett). */
  authDeleted: boolean;
  /** A `users` profil már nem létezik (töröltük, vagy nem is volt). `false`: még megvan. */
  profileDeleted: boolean;
  /** A profilkép már nincs a tárolóban (töröltük, vagy nem is volt). `false`: ott maradt. */
  photoDeleted: boolean;
  /**
   * A fiók törlődött, de a takarítás (profil vagy kép) nem ért véget: egy mondat, amit a
   * felület kiír. Legtöbbször a szerver trigger befejezi, és a mondat ezt is kimondja.
   */
  warning?: string;
}

/**
 * „Ennek a személynek ne legyen fiókja többé." Az admin Felhasználók listájáról.
 *
 * A TELJES műveletet a szerver végzi (`functions/src/users.js`): előbb az Auth-fiókot törli,
 * utána takarít (sírkő, `users/{uid}` profil, profilkép). A foglalások és a számlák maradnak
 * (számviteli megőrzés; a nevet és az e-mail címet a saját másolatukban hordozzák).
 *
 * Korábban a lista csak a `users` dokumentumot törölte a böngészőből: az Auth-fiók megmaradt,
 * az illető tovább belépett, és a belépéskori `createOrUpdateUser` újra létrehozta a profilt.
 *
 * Admin fiókot és a hívó saját fiókját a szerver elutasítja (`failed-precondition`, magyar
 * mondattal, az admin esetében szó szerint a `lib/accountDeletion.ts` mondatával): a jog
 * elvétele a Felhasználók lista admin-kapcsolójáé (`revokeAdminAccess`).
 */
export const deleteUserAccount = async (params: {
  uid: string;
}): Promise<DeleteUserAccountResult> => {
  const deleteUserAccountFn = httpsCallable<typeof params, DeleteUserAccountResult>(
    functions,
    "deleteUserAccount",
  );
  const result = await deleteUserAccountFn(params);
  return result.data;
};

// Felhasználó létrehozása vagy frissítése
export const createOrUpdateUser = async (user: { uid: string, email: string | null, displayName?: string | null }): Promise<void> => {
  if (!user.email) return;
  
  const userRef = doc(db, "users", user.uid);
  const userDoc = await getDoc(userRef);
  
  if (!userDoc.exists()) {
    // Ha a felhasználó még nem létezik, létrehozzuk
    await setDoc(userRef, {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || null,
      photoURL: null,
      createdAt: serverTimestamp(),
      isAdmin: false,
      allowCashPayment: false // Alapértelmezetten nem engedélyezett a helyszíni fizetés
    });
  }
};

/**
 * A felhasználó SAJÁT nevének mentése a profil oldalról.
 *
 * Nem a `createOrUpdateUser` végzi: az szándékosan csak LÉTREHOZ (belépéskor fut, és egy
 * meglévő rekordot nem írhat felül - az admin által javított nevet minden bejelentkezés
 * visszaállítaná a régi Auth-névre). Ez a függvény ezért célzottan egyetlen mezőt ír.
 *
 * A hiányzó dokumentumot viszont előbb a TELJES mezőkészlettel hozatjuk létre: egy
 * önmagában álló `merge` fél rekordot hagyna (`createdAt`, `isAdmin`, `allowCashPayment`
 * nélkül), és mivel onnantól létezne, egyetlen későbbi belépés sem pótolná a hiányzókat.
 * Ez a helyzet ritka, de valós: a belépéskori `createOrUpdateUser` hibáját elnyeljük.
 */
export const updateUserDisplayName = async (
  user: { uid: string; email: string | null },
  displayName: string,
  /** A név két része: a következő űrlap ezekből tölt elő tippelés helyett (`name.ts`) */
  nameParts?: PersonName,
): Promise<void> => {
  await createOrUpdateUser({ uid: user.uid, email: user.email, displayName });
  await setDoc(
    doc(db, "users", user.uid),
    {
      displayName,
      ...(nameParts ? { lastName: nameParts.lastName, firstName: nameParts.firstName } : {}),
    },
    { merge: true },
  );
};

/**
 * A vendég profil-dokumentuma, valós időben. A mezők NYERS, tárolt alakban jönnek (a telefon
 * E.164-ben): a megjelenítési alakra hozás a `guestDetails.ts` dolga, és szándékosan ott is
 * marad - lásd a fenti típus-import indoklását. A hívó a `useGuestDetails` hook.
 */
export const subscribeToUserProfile = (
  userId: string,
  onChange: (profile: DocumentData) => void,
  onError: (error: Error) => void,
): (() => void) =>
  onSnapshot(doc(db, "users", userId), (snapshot) => onChange(snapshot.data() ?? {}), onError);

/**
 * A vendég kapcsolati és számlázási adatainak mentése - a `updateUserDisplayName` párja, és
 * ugyanazért kezdi a teljes rekord létrehozásával: egy önmagában álló `merge` fél profilt
 * hagyna (`createdAt`, `isAdmin`, `allowCashPayment` nélkül), amit onnantól egyetlen későbbi
 * belépés sem pótolna.
 *
 * Részhalmazt vár, TÁROLT alakban (a `saveGuestDetails` normalizál): a profil két dialógusa
 * csak a saját mezőit írja, a foglalás pedig csak azt, amit a vendég tényleg megadott - így
 * egy magánszemélyként leadott foglalás nem törli a korábban mentett számlázási adatokat.
 */
export const saveUserDetails = async (
  user: { uid: string; email: string | null },
  details: StoredGuestDetails,
): Promise<void> => {
  await createOrUpdateUser({ uid: user.uid, email: user.email });
  await setDoc(doc(db, "users", user.uid), details, { merge: true });
};

// Felhasználó foglalásainak lekérése (userId és email alapján is keres)
// A felhasználó foglalásai két forrásból jönnek: a userId szerintiek és - ha van e-mail - az
// admin által e-mail címre rögzítettek. A két lista azonosító szerint egyesül.
const mergeUserBookings = (sources: Booking[][], includeDeleted: boolean): Booking[] => {
  const byId = new Map<string, Booking>();
  for (const booking of sources.flat()) byId.set(booking.id, booking);
  return Array.from(byId.values())
    .filter(booking => includeDeleted || !booking.deletedAt)
    // Két lekérdezés uniója: a rendezés (dátum, majd kezdés szerint csökkenő) csak itt lehetséges.
    // String(): egy hibás (kézzel szerkesztett) dokumentum ne dobja el az egész listát
    .sort(
      (a, b) =>
        String(b.date ?? "").localeCompare(String(a.date ?? "")) ||
        String(b.startTime ?? "").localeCompare(String(a.startTime ?? "")),
    );
};

// A felhasználó foglalásainak valós idejű figyelése (a subscribeToBookingsWithTemporary párja
// a vendégoldalra). A lokális cache miatt visszatéréskor azonnal megvan a lista.
export const subscribeToBookingsByUser = (
  userId: string,
  userEmail: string | undefined,
  onChange: (bookings: Booking[]) => void,
  onError: (error: Error) => void,
  { includeDeleted = false }: BookingsSubscriptionOptions = {},
): (() => void) => {
  const bookingsCollection = collection(db, "bookings");
  // Csak egyenlőség-szűrők, orderBy nélkül: nem kell hozzájuk összetett index, a sorrendet
  // úgyis az egyesítés adja
  const queries = [
    query(bookingsCollection, where("userId", "==", userId)),
    ...(userEmail ? [query(bookingsCollection, where("email", "==", userEmail))] : []),
  ];
  /**
   * Az e-mail szerinti ág a régi, fiók nélkül leadott foglalásokat párosítja a vendéghez.
   * A `bookings` olvasásának szűkítése után (`docs/public-booking-reads.md`) ezt az ágat a
   * szabály csak MEGERŐSÍTETT e-mail címre engedheti (a `bookings` szabály
   * `isBookingOwner()` függvénye ugyanezt a mércét használja: meg nem erősített címmel
   * bárki regisztrálhatna más címével). Megerősítetlen címnél tehát a figyelő
   * `permission-denied`-del elhal - ez NEM hiba, hanem a szabály döntése: az ág üresen
   * marad, a vendég a saját fiókjához kötött foglalásait változatlanul látja.
   */
  const EMAIL_QUERY_INDEX = 1;
  const results: (Booking[] | null)[] = queries.map(() => null);

  // Egy Firestore-figyelő hibája végleges. Ha az egyik lekérdezés elhal, a másik adatát
  // továbbra is kiadjuk - de a hibát minden adag után újrajelezzük, különben a következő
  // sikeres adat elfedné, és a hiányos lista teljesnek látszana.
  let failure: Error | null = null;

  // Csak akkor adunk listát, ha minden forrás megszólalt - különben az első forrás fél
  // listája villanna fel, majd ugrana át a teljesre. A cache-ből érkező adag is válasz:
  // ezen múlik, hogy visszatéréskor azonnal látszik a lista.
  const emit = () => {
    if (results.some(result => result === null)) return;
    onChange(mergeUserBookings(results as Booking[][], includeDeleted));
    if (failure) onError(failure);
  };

  const unsubscribes = queries.map((userQuery, index) =>
    onSnapshot(
      userQuery,
      (snapshot) => {
        results[index] = snapshot.docs.map(docSnap => convertTimestampFields(docSnap.data(), docSnap.id));
        emit();
      },
      (error) => {
        // Az elhalt forrás addig ismert adata marad; ha még nem volt, üresnek számít - így
        // egy halott figyelő nem fogja vissza a másikat
        const isBlockedEmailBranch =
          index === EMAIL_QUERY_INDEX &&
          (error as { code?: string }).code === "permission-denied";
        if (isBlockedEmailBranch) {
          console.info("A foglalások e-mail szerinti ága nem engedélyezett (nincs megerősítve a cím)");
        } else {
          failure = error;
          onError(error);
        }
        results[index] ??= [];
        emit();
      },
    ),
  );

  return () => unsubscribes.forEach(unsubscribe => unsubscribe());
};

// Foglalás lemondása
export const cancelBooking = async (id: string): Promise<void> => {
  const docRef = doc(db, "bookings", id);
  await updateDoc(docRef, {
    bookingStatus: "cancelled",
    updatedAt: serverTimestamp()
  });
};

// Zárolás-tulajdonos token: böngészőfülenként egyedi (sessionStorage), és TITOK - csak
// hívás törzsében utazik, a dokumentumba a Cloud Function kizárólag a hash-ét írja.
// A szerver ez alapján cseréli le a vendég összes korábbi kijelölési zárolását
// (párhuzamos kattintások és újratöltés után is), a foglalási űrlap pedig a hash alapján
// nem tekinti foglaltságnak a saját zárolásait.
let lockOwnerTokenCache: string | null = null;
export const getLockOwnerToken = (): string => {
  if (lockOwnerTokenCache) return lockOwnerTokenCache;
  const key = "lockOwnerToken";
  const isValid = (value: string | null): value is string =>
    !!value && /^[A-Za-z0-9_-]{8,64}$/.test(value);
  let token: string | null = null;
  try {
    token = sessionStorage.getItem(key);
  } catch {
    token = null;
  }
  if (!isValid(token)) {
    token =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID().replace(/-/g, "")
        : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
    try {
      sessionStorage.setItem(key, token);
    } catch {
      // sessionStorage nélkül a token csak erre az oldalbetöltésre él
    }
  }
  lockOwnerTokenCache = token;
  return token;
};

// A token SHA-256 hash-e: ez az, ami a dokumentumokban szerepel, tehát ezzel ismerjük
// fel a saját zárolásainkat a nyilvánosan olvasható temporary_bookings kollekcióban.
let lockOwnerHashPromise: Promise<string | null> | null = null;
export const getLockOwnerHash = (): Promise<string | null> => {
  if (!lockOwnerHashPromise) {
    lockOwnerHashPromise = (async () => {
      try {
        const data = new TextEncoder().encode(getLockOwnerToken());
        const digest = await crypto.subtle.digest("SHA-256", data);
        return Array.from(new Uint8Array(digest))
          .map((byte) => byte.toString(16).padStart(2, "0"))
          .join("");
      } catch {
        // Nem biztonságos kontextus (nincs crypto.subtle): a saját zárolást ilyenkor
        // csak dokumentum-azonosító alapján ismerjük fel
        return null;
      }
    })();
  }
  return lockOwnerHashPromise;
};

// Ideiglenes foglalás létrehozása
export const createTemporaryBooking = async (
  bookingData: {
    date: string;
    startTime: string;
    endTime: string;
    lanes: number[];
    laneTimeSlots?: LaneTimeSlot[];
    userId?: string;
  }
): Promise<string> => {
    // A lejáratot (10 perc) a szerver szabja meg, a kliens nem küldi: régen a böngésző írta,
    // és bárki tetszőleges lejáratú zárolást kérhetett (`functions/src/lib/temporaryLocks.js`).
    // TELEPÍTÉSI SORREND: a régi szerver (a 2026-09-13-i build) `expiresAt` nélkül 60 percet
    // adna, ezért ez a kliens csak a függvények UTÁN mehet ki (előbb functions, utána hosting)
    //
    // A userId kezelése - ha undefined vagy üres, ne tegyük be a dokumentumba
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let docData: any;
    
    if (!bookingData.userId) {
      // userId kihagyása, ha nem definiált vagy üres
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { userId, ...bookingDataWithoutUser } = bookingData;
      docData = { ...bookingDataWithoutUser };
    } else {
      docData = { ...bookingData };
    }

    // A korábbi zárolásainkat a szerver a token alapján ismeri fel, zárja ki az
    // ütközés-ellenőrzésből és törli ugyanabban a tranzakcióban
    docData.lockOwnerToken = getLockOwnerToken();
    
    // Cloud Function meghívása a temporaryBooking létrehozásához
    // A Cloud Function tranzakciót használ, ami megakadályozza a versenyhelyzeteket (race condition)
    const createTempBookingFn = httpsCallable(functions, 'createTemporaryBooking');
    const result = await createTempBookingFn(docData);
    const data = result.data as { id: string };
    return data.id;
};

// Ideiglenes foglalás törlése
export const deleteTemporaryBooking = async (id: string): Promise<void> => {
  // A temp_ előtagú azonosító fizetési zárolás: azt a Teya callback törli, a nyilvános
  // végpont szándékosan elutasítja (különben bárki felszabadíthatná más fizetés alatti
  // pályáját). Itt nincs teendő.
  if (id.startsWith('temp_')) {
    return;
  }

  // Cloud Function meghívása a temporaryBooking törléséhez. A tulajdonos tokent is
  // elküldjük: a szerver csak a saját zárolásunkat törli.
  // A hibát NEM nyeljük el: a hívó így tudja, hogy a zárolás még él - különben
  // jelzés nélkül fogná a pályát a lejáratáig.
  const deleteTempBookingFn = httpsCallable(functions, 'deleteTemporaryBooking');
  await deleteTempBookingFn({ id, lockOwnerToken: getLockOwnerToken() });
};

// ---------------------------------------------------------------------------
// Számlázás
//
// Négy gyűjtemény, egy lista. A kiállított számlákat (`invoices`) és a helyi
// piszkozatokat (`invoiceDrafts`) a `subscribeToInvoiceSources` fésüli össze - ugyanaz a
// fogás, mint a foglalásoknál a `subscribeToBookingSources`: két figyelő, egy kimenet, és
// ha az egyik forrás elhasal, a másik adata akkor is kimegy. A tétel- és vevő-memóriát
// (`invoiceItems`, `invoicePartners`) a szerver tanulja kiállításkor, a kliens csak olvassa.
//
// Ami pénz, az szerveren dől el: a kiállítás, a sztornó, a PDF és az e-mail mind callable, a
// régió-kötött `functions` exporttal (soha nem `getFunctions()` régió nélkül - az némán a
// us-central1-re menne). A Billingo-szinkront a kliens nem hívja: az óránként magától fut.
// ---------------------------------------------------------------------------

const INVOICE_TIMESTAMP_FIELDS = ["createdAt", "updatedAt", "issuedAt", "cancelledAt", "lastUsedAt"];

/*
  A KLIENS HATÁRIDEJE KÖVESSE A SZERVERÉT. A callable SDK alapértelmezése 70 mp, a
  számlázó függvényeké viszont 540 / 120 - a kettő közti ablakban a szerver MÉG
  DOLGOZIK, a kliens viszont már `deadline-exceeded`-del elszáll. A kiállításnál ez a
  legdrágább: 70 mp-nél a képernyő azt mondta, „a művelet nem sikerült", miközben a számla
  épp készült, és a Kiállítás gomb ott maradt élőben a legveszélyesebb dialóguson. Új számla
  ettől sem született (a zár fogja), de a hazugság és az ijedtség így is elkerülhető.
  A számok SZÁNDÉKOSAN egyeznek a `functions/src/invoices.js` `timeoutSeconds` értékeivel -
  az eladó adatait kérő `getInvoiceIssuer` is (60 mp: két rövid Billingo-olvasás fér bele).
*/
const ISSUE_TIMEOUT_MS = 540_000;
const CANCEL_TIMEOUT_MS = 120_000;
const DOCUMENT_TIMEOUT_MS = 120_000;
const ISSUER_TIMEOUT_MS = 60_000;

/** A Timestamp mezők ISO szöveggé - a `convertTimestampFields` számla-oldali párja. */
const withIsoTimestamps = <T>(id: string, data: DocumentData): T => {
  const formatted: Record<string, unknown> = { ...data, id };
  for (const field of INVOICE_TIMESTAMP_FIELDS) {
    const iso = toIsoString(data[field]);
    if (iso !== undefined) formatted[field] = iso;
  }
  return formatted as T;
};

/**
 * A kiállított számlák és a helyi piszkozatok egy listában, valós időben.
 *
 * A piszkozat `isDraft: true` jelzővel jön (`draftToInvoice`), és ELÖL - a lista úgyis a
 * saját rendezését alkalmazza, de az alapértelmezett sorrendben a legfrissebb, még be nem
 * fejezett munka legyen legfelül. Az első kiadás csak akkor történik meg, ha mindkét forrás
 * megérkezett; egy végleg leállt figyelő után viszont a másik adatát akkor is kiadjuk,
 * különben a lista tartósan üresen maradna.
 *
 * A SÉRÜLT FORRÁS HIBÁJA A KIADOTT ADAG UTÁN MEGY KI, és minden további adag után újra -
 * pontosan úgy, ahogy a `useSubscription` a több forrásból dolgozó feliratkozástól várja.
 * Fordított sorrendben a hiba nyomtalanul eltűnne (a friss adatot a hook sikernek veszi),
 * és a képernyőn „Még nincs egyetlen számla sem" állna, miközben a jogosultság hiányzik -
 * a könyvelés oldalán ez a hazugság drága.
 */
export const subscribeToInvoiceSources = (
  onChange: (invoices: Invoice[]) => void,
  onError: (error: Error) => void,
): (() => void) => {
  /*
    A számlák szótárát MENET KÖZBEN töltjük be, nem a modul tetején: lásd az import
    megjegyzését - az `AuthContext` a `services.ts`-t eagerly húzza be, tehát egy statikus
    import ezt a kódot a nyitóoldal csomagjába tenné. A feliratkozás ettől nem lesz lassabb:
    a Számlák szekció maga is lazán töltődik, és a leiratkozás a betöltés közben is működik
    (`cancelled`), tehát egy gyors mount/unmount nem hagy maga után élő figyelőt.
  */
  let cancelled = false;
  let detach: (() => void) | null = null;

  void import("../invoicing/invoices")
    .then(({ draftToInvoice }) => {
      if (!cancelled) detach = attachInvoiceSources(draftToInvoice, onChange, onError);
    })
    .catch((error: Error) => {
      if (!cancelled) onError(error);
    });

  return () => {
    cancelled = true;
    detach?.();
  };
};

const attachInvoiceSources = (
  draftToInvoice: (draft: InvoiceDraft) => Invoice,
  onChange: (invoices: Invoice[]) => void,
  onError: (error: Error) => void,
): (() => void) => {
  let issued: Invoice[] | null = null;
  let drafts: Invoice[] | null = null;
  let issuedError: Error | null = null;
  let draftsError: Error | null = null;

  const emit = () => {
    if ((issued !== null || issuedError) && (drafts !== null || draftsError)) {
      onChange([...(drafts ?? []), ...(issued ?? [])]);
    }
    // Amíg egy forrás sérült, a hiba az adat UTÁN megy ki: így a nézet hibát mutat, nem
    // egy csonka (vagy üres) listát „kész" állapotként. A toast a hookban csak egyszer szól.
    const failure = issuedError ?? draftsError;
    if (failure) onError(failure);
  };

  const handleError = (source: "invoices" | "drafts") => (error: Error) => {
    if (source === "invoices") {
      issuedError = error;
    } else {
      draftsError = error;
    }
    emit();
  };

  const unsubscribeInvoices = onSnapshot(
    query(collection(db, "invoices")),
    (snapshot) => {
      issued = snapshot.docs.map((docSnap) => withIsoTimestamps<Invoice>(docSnap.id, docSnap.data()));
      issuedError = null;
      emit();
    },
    handleError("invoices"),
  );

  const unsubscribeDrafts = onSnapshot(
    query(collection(db, "invoiceDrafts")),
    (snapshot) => {
      drafts = snapshot.docs.map((docSnap) =>
        draftToInvoice(withIsoTimestamps<InvoiceDraft>(docSnap.id, docSnap.data())),
      );
      draftsError = null;
      emit();
    },
    handleError("drafts"),
  );

  return () => {
    unsubscribeInvoices();
    unsubscribeDrafts();
  };
};

/** Egy piszkozat-pillanatkép eredete - a szerkesztő ebből dönti el, rögzítheti-e már. */
export interface InvoiceDraftSnapshotMeta {
  /**
   * A pillanatkép a helyi gyorsítótárból jött, a szerver még nem erősítette meg. A
   * `persistentLocalCache` miatt az első kép ilyen is lehet - és ELAVULT is: egy máshol
   * módosított, sőt közben kiállított (tehát a szerveren már törölt) piszkozat régi másolata.
   */
  fromCache: boolean;
}

/**
 * EGY helyi piszkozat valós időben - a szerkesztő „mentett piszkozat" útja ebből tölt.
 *
 * `null`, ha a piszkozat nem létezik: közben kiállították (a szerver a kiállítással EGY
 * tranzakcióban törli), vagy valaki törölte. A '/'-t tartalmazó azonosító ugyanúgy az
 * `onError`-ra kerül, mint a `subscribeToBooking`-nál - a `doc()` szinkron dobása nem
 * vihet el egy oldalt.
 *
 * Minden képhez megmondja, honnan jött (`fromCache`): a szerkesztő CSAK szerver-megerősített
 * képre rögzít, a gyorsítótárét csak rövid várakozás után fogadja el (`InvoiceEditorPage`).
 */
export const subscribeToInvoiceDraft = (
  draftId: string,
  onChange: (draft: InvoiceDraft | null, meta: InvoiceDraftSnapshotMeta) => void,
  onError: (error: Error) => void,
): (() => void) => {
  if (!draftId || draftId.includes("/")) {
    onError(new Error(`Érvénytelen piszkozat-azonosító: ${draftId}`));
    return () => {};
  }

  try {
    return onSnapshot(
      doc(db, "invoiceDrafts", draftId),
      // A metaadat-változás is kell: a gyorsítótár képe és a szerver megerősítése közt
      // gyakran CSAK a `fromCache` tér el - enélkül a megerősítés sosem érkezne meg
      { includeMetadataChanges: true },
      (snapshot) =>
        onChange(
          snapshot.exists() ? withIsoTimestamps<InvoiceDraft>(snapshot.id, snapshot.data()) : null,
          { fromCache: snapshot.metadata.fromCache },
        ),
      onError,
    );
  } catch (error) {
    onError(error instanceof Error ? error : new Error(String(error)));
    return () => {};
  }
};

/**
 * Ennyi tételt és vevőt hozunk le a memóriából, HASZNÁLAT SZERINT a legnépszerűbbeket.
 *
 * A két gyűjtemény korábban korlát nélkül jött le - minden alkalommal, amikor egy admin
 * megnyitotta a Számlák szekciót. Egy bowlingpályánál ez évekig nem fáj, de a memória
 * MONOTON NŐ (soha nem törlünk belőle), tehát a második-harmadik évtől minden hidegindítás
 * egyre több száz dokumentumot húz le azért, hogy egy legördülő lista tetején öt sor
 * megjelenjen. A 200 bőven a „mindent felajánl, amit valaha használtál" fölött van: a 201.
 * leggyakrabban számlázott tétel felajánlása már senkinek nem segít, begépelni gyorsabb.
 */
const INVOICE_MEMORY_LIMIT = 200;

/**
 * Használat szerinti rangsor a memória-gyűjteményekhez - a lehozott adagon BELÜL.
 *
 * A Firestore `orderBy("usageCount")` adja a KERETET (különben a `limit` a dokumentum-
 * azonosító, azaz egy sha1 szerint vágna, tehát véletlenszerűen - épp a leggyakoribb
 * tételek eshetnének ki). A finomabb sorrend (azonos darabszámnál a frissebb, majd a
 * névsor) marad itt: azt egy Firestore rendezés összetett indexet kívánna.
 *
 * A „hiányzó mezőre kimarad a rekord" kockázat itt nem áll fenn: ezekbe a gyűjteményekbe
 * KIZÁRÓLAG az Admin SDK ír (`allow write: if false`), és mindig `increment`-tel - tehát
 * `usageCount` minden rekordon van.
 */
const byUsage = <T extends { name: string; usageCount?: number; lastUsedAt?: string }>(
  a: T,
  b: T,
): number =>
  (b.usageCount ?? 0) - (a.usageCount ?? 0) ||
  (b.lastUsedAt ?? "").localeCompare(a.lastUsedAt ?? "") ||
  a.name.localeCompare(b.name, "hu");

/** Tétel-memória: amit egyszer kiszámláztunk, azt a szerkesztő felajánlja. */
export const subscribeToInvoiceItems = (
  onChange: (items: SavedInvoiceItem[]) => void,
  onError: (error: Error) => void,
): (() => void) =>
  onSnapshot(
    query(collection(db, "invoiceItems"), orderBy("usageCount", "desc"), limit(INVOICE_MEMORY_LIMIT)),
    (snapshot) =>
      onChange(
        snapshot.docs
          .map((docSnap) => withIsoTimestamps<SavedInvoiceItem>(docSnap.id, docSnap.data()))
          .sort(byUsage),
      ),
    onError,
  );

/** Vevő-memória: ugyanez a vevőkre. */
export const subscribeToInvoicePartners = (
  onChange: (partners: SavedInvoicePartner[]) => void,
  onError: (error: Error) => void,
): (() => void) =>
  onSnapshot(
    query(collection(db, "invoicePartners"), orderBy("usageCount", "desc"), limit(INVOICE_MEMORY_LIMIT)),
    (snapshot) =>
      onChange(
        snapshot.docs
          .map((docSnap) => withIsoTimestamps<SavedInvoicePartner>(docSnap.id, docSnap.data()))
          .sort(byUsage),
      ),
    onError,
  );

/** Amit a még sosem futott szinkron ad: nincs sikeres futás, tehát elakadtnak számít. */
const NEVER_SYNCED: InvoiceSyncStatus = { lastRunAt: null, truncated: false, sweepTruncated: false };

/**
 * A Billingo-szinkron állapota valós időben (`invoiceSync/billingo`) - a Számlák lista
 * figyelmeztetése ebből dolgozik (`InvoiceSyncAlert`, értelmezés: `invoicing/syncStatus.ts`).
 *
 * A dokumentumot a szerver CSAK sikeres futás végén írja, ezért három mezője elég: a
 * `lastRunAt` (a régi érték maga a hiba jele) és a `lastResult` két csonkolás-jelzője. A
 * `lastResult` többi mezőjét szándékosan nem olvassuk (lásd `InvoiceSyncStatus`).
 *
 * A HIÁNYZÓ dokumentum sem `null`, hanem „még sosem futott" (`NEVER_SYNCED`): a
 * `useSubscription` a `null` adatot töltésnek veszi, tehát egy üres projekt örökké töltene -
 * holott épp azt kell kimondani, hogy a szinkron egyszer sem ért célba.
 *
 * CSAK SZERVER-MEGERŐSÍTETT KÉPET adunk ki. A `persistentLocalCache` az előző látogatás
 * másolatát adná elsőnek - egy tegnapi `lastRunAt`-tal, amire a lista egy pillanatra piros
 * „nem fut" figyelmeztetést villantana minden megnyitáskor, mielőtt a szerver felülírja.
 * Ezért kell az `includeMetadataChanges`: ha a szerver ugyanazt mondja, mint a gyorsítótár,
 * CSAK a `fromCache` változik, és enélkül a megerősítés meg sem érkezne. Offline így nincs
 * adat, a figyelmeztetés hallgat - a hook ezt töltésnek látja, nem hibának.
 *
 * A `doc()` szinkron dobása (és minden más hiba) az `onError`-ra kerül, mint a
 * `subscribeToBooking`-nál: ez egy csendes, mellékes figyelő, nem vihet el egy oldalt.
 */
export const subscribeToInvoiceSyncStatus = (
  onChange: (status: InvoiceSyncStatus) => void,
  onError: (error: Error) => void,
): (() => void) => {
  try {
    return onSnapshot(
      doc(db, "invoiceSync", "billingo"),
      { includeMetadataChanges: true },
      (snapshot) => {
        if (snapshot.metadata.fromCache) return;
        if (!snapshot.exists()) {
          onChange(NEVER_SYNCED);
          return;
        }
        const data = snapshot.data();
        const lastResult: DocumentData =
          data.lastResult && typeof data.lastResult === "object" ? data.lastResult : {};
        onChange({
          // Csak valódi `Timestamp`: a szerver `serverTimestamp()`-pel írja, más alak hibás adat,
          // és az a „még sosem futott" oldalra esik - inkább egy téves figyelmeztetés, mint egy
          // téves megnyugtatás
          lastRunAt: data.lastRunAt instanceof Timestamp ? data.lastRunAt.toDate() : null,
          truncated: lastResult.truncated === true,
          sweepTruncated: lastResult.sweepTruncated === true,
        });
      },
      onError,
    );
  } catch (error) {
    onError(error instanceof Error ? error : new Error(String(error)));
    return () => {};
  }
};

/**
 * A sztornózott számlák azonosítói valós időben: a pályanézet ebből tudja, hogy egy foglalás
 * `invoiceIds`-ében álló számla még érvényes-e (`hasBookingInvoice`).
 *
 * Csak a sztornózottakat figyeljük, nem az összes számlát: a foglalás nézetnek ennyi kell, és
 * ez a halmaz a sztornók számával nő, nem a forgalommal (2026-10-08: egy a 49-ből). Az eredeti
 * számla `status: "cancelled"` állapotát a sztornó és a szinkron is írja.
 *
 * A `query()` szinkron dobása is az `onError`-ra kerül: ez egy csendes, mellékes figyelő.
 */
export const subscribeToCancelledInvoiceIds = (
  onChange: (ids: ReadonlySet<string>) => void,
  onError: (error: Error) => void,
): (() => void) => {
  // Csak valódi változásra szólunk: egy új, de ugyanolyan halmaz a pályanézet teljes rácsát
  // rajzoltatná újra (a halmaz a számlázási összefüggés része)
  let previous: ReadonlySet<string> | null = null;
  try {
    return onSnapshot(
      query(collection(db, "invoices"), where("status", "==", "cancelled")),
      (snapshot) => {
        const ids = new Set(snapshot.docs.map((docSnap) => docSnap.id));
        if (previous && previous.size === ids.size && [...ids].every((id) => previous?.has(id))) {
          return;
        }
        previous = ids;
        onChange(ids);
      },
      onError,
    );
  } catch (error) {
    onError(error instanceof Error ? error : new Error(String(error)));
    return () => {};
  }
};

/**
 * Firestore nem fogad `undefined` értéket (a projektben nincs `ignoreUndefinedProperties`),
 * a piszkozat viszont félkész is lehet. A felső szintet ezért megszűrjük - mélyebben nem
 * keletkezik `undefined`, mert a `toInvoiceRecord` az üres mezőket ki sem írja.
 */
const withoutUndefined = <T extends object>(record: T): Record<string, unknown> =>
  Object.fromEntries(Object.entries(record).filter(([, value]) => value !== undefined));

/**
 * Egy helyi piszkozat mentése. Az azonosítót és a `draftKey`-t a hívó adja
 * (`toInvoiceRecord`), és a kulcs a piszkozat élete végéig ugyanaz marad: ezen múlik, hogy
 * két kiállítási próbálkozásból ne legyen két számla. A kiállítás a SZERVEREN törli a
 * piszkozatot, ugyanabban a tranzakcióban, amelyben a számlát rögzíti.
 *
 * TELJES CSERE, nem `merge`. A `toInvoiceRecord` a TELJES rekordot adja (a keltet és a kulcsot
 * is), az üres elhagyható mezőket viszont KIHAGYJA - összefésülve tehát egy kiürített
 * e-mail cím, adószám, telefonszám vagy megjegyzés a régi mentésből támadt fel, és a
 * kiállításnál is az ment volna ki. A dokumentumra más nem ír (a szerver csak törli), tehát a
 * csere semmit nem visz el, amit a rekord nem hordoz. Az `updatedAt`-et ez a függvény teszi rá.
 */
export const saveInvoiceDraft = async (draft: InvoiceDraft): Promise<void> => {
  await setDoc(doc(db, "invoiceDrafts", draft.id), {
    ...withoutUndefined(draft),
    updatedAt: serverTimestamp(),
  });
};

/**
 * Piszkozatok törlése kötegelve - ugyanaz a szabály, mint a foglalásoknál: a `writeBatch`
 * kötegen belül atomi, a kötegek egymás után mennek, és a hívó azt kapja vissza, mi
 * sikerült. Nem dob: beszámol.
 */
export const deleteInvoiceDrafts = async (
  ids: readonly string[],
): Promise<{ deletedIds: string[]; failedIds: string[] }> => {
  const deletedIds: string[] = [];
  const failedIds: string[] = [];

  for (let start = 0; start < ids.length; start += DELETE_BATCH_SIZE) {
    const chunk = ids.slice(start, start + DELETE_BATCH_SIZE);
    const batch = writeBatch(db);
    chunk.forEach((id) => batch.delete(doc(db, "invoiceDrafts", id)));

    try {
      await batch.commit();
      deletedIds.push(...chunk);
    } catch (error) {
      console.error("Hiba a számla-piszkozatok kötegelt törlése közben:", error);
      failedIds.push(...chunk);
    }
  }

  return { deletedIds, failedIds };
};

export interface IssueInvoiceResult {
  invoiceId: string;
  billingoId?: number;
  invoiceNumber: string;
  pdfUrl?: string;
  /** `true`, ha ugyanezzel a `draftKey`-jel már kiállítottuk - a zár ismerte fel. */
  alreadyIssued?: boolean;
  /** Amit a kiállítás során észrevettünk, de nem akasztotta meg (pl. az e-mail nem ment ki). */
  warnings?: string[];
}

/**
 * Számla kiállítása.
 *
 * A `draftKey` az idempotencia-kulcs: a szerver ezzel foglal zárat, tehát ugyanazzal a
 * kulccsal küldött második hívásból nem lesz második számla - visszakapjuk az elsőt
 * `alreadyIssued` jelzéssel. A kulcs a piszkozattal EGYÜTT születik és nem változik,
 * ezért magából a piszkozatból vesszük; a `draftId` mondja meg, melyik mentett
 * piszkozatot kell a kiállítással EGY tranzakcióban törölni.
 *
 * A hibát NEM nyeljük el. Újrapróbálni legfeljebb kétszer szabad, és CSAK hálózati hibán:
 * a `failed-precondition` azt jelenti, hogy a Billingónál már megvan a számla, csak nálunk
 * hasalt el a rögzítés - azt újraküldeni duplikálna.
 */
export const issueInvoice = async ({
  draft,
  draftId,
  bookingId,
  allowAdditional,
}: {
  draft: InvoiceDraft;
  /** A MENTETT piszkozat azonosítója, ha a számla abból indult. */
  draftId?: string;
  bookingId?: string;
  /**
   * Tudatosan állítunk ki TOVÁBBI számlát egy már számlázott foglalásra.
   *
   * A szerver alapból elutasítja az ilyet (`already-issued`, még a Billingo-hívás ELŐTT), és
   * csak a szigorú `=== true` nyitja ki. Ezért ez sosem mehet ki magától: kizárólag akkor,
   * ha az admin a megerősítőben KÜLÖN rábólintott a második számlára - a „Újabb számla"
   * gombfelirat önmagában nem megerősítés.
   */
  allowAdditional?: boolean;
}): Promise<IssueInvoiceResult> => {
  const issueInvoiceFn = httpsCallable(functions, "issueInvoice", { timeout: ISSUE_TIMEOUT_MS });
  const result = await issueInvoiceFn({
    draftKey: draft.draftKey,
    draft,
    ...(draftId ? { draftId } : {}),
    ...(bookingId ? { bookingId } : {}),
    ...(allowAdditional ? { allowAdditional: true } : {}),
  });
  return result.data as IssueInvoiceResult;
};

export interface CancelInvoiceResult {
  cancelInvoiceId: string;
  invoiceNumber: string;
  /** `true`, ha ezt a számlát már sztornóztuk - a zár ismerte fel, új sztornó nem készült. */
  alreadyCancelled?: boolean;
  /** Ahova a sztornó ténylegesen kiment. Üres, ha nem kértük a kiküldését. */
  sent?: string[];
  /** Ami a sztornó UTÁN nem sikerült (jellemzően az e-mail) - a sztornó ettől még megvan. */
  warnings?: string[];
}

/**
 * Sztornó számla. A sztornózott számlára a szerver írja vissza a `cancelledByInvoiceId`-t.
 *
 * Az `emails` a sztornó KIKÜLDÉSE a vevőnek - a szerver a sztornó UTÁN, külön hívásban küldi
 * el, tehát egy elhasalt levél sosem buktat el egy sztornót: olyankor a `warnings` mondja ki,
 * hogy a sztornó megvan, de a levél nem ment ki. A `sent` az, ahova tényleg kiment.
 */
export const cancelInvoice = async (
  invoiceId: string,
  { emails }: { emails?: readonly string[] } = {},
): Promise<CancelInvoiceResult> => {
  const cancelInvoiceFn = httpsCallable(functions, "cancelInvoice", { timeout: CANCEL_TIMEOUT_MS });
  const result = await cancelInvoiceFn({
    invoiceId,
    ...(emails?.length ? { emails: [...emails] } : {}),
  });
  return result.data as CancelInvoiceResult;
};

/**
 * A számla PDF-je. A Billingo a frissen kiállított számla PDF-jére 202-vel felelhet
 * („még készül"), ezt a szerver kezeli - a válaszban letöltési URL vagy base64 jön.
 */
export const getInvoicePdf = async (
  invoiceId: string,
): Promise<{ url?: string; pdfBase64?: string; warnings?: string[] }> => {
  const getInvoicePdfFn = httpsCallable(functions, "getInvoicePdf", { timeout: DOCUMENT_TIMEOUT_MS });
  const result = await getInvoicePdfFn({ invoiceId });
  return result.data as { url?: string; pdfBase64?: string; warnings?: string[] };
};

/** A számla elküldése e-mailben. A válasz azt sorolja fel, MELY címekre ment ki. */
export const sendInvoiceEmail = async (
  invoiceId: string,
  emails: readonly string[],
): Promise<{ sent: string[]; warnings?: string[] }> => {
  const sendInvoiceEmailFn = httpsCallable(functions, "sendInvoiceEmail", { timeout: DOCUMENT_TIMEOUT_MS });
  const result = await sendInvoiceEmailFn({ invoiceId, emails: [...emails] });
  return result.data as { sent: string[]; warnings?: string[] };
};

/**
 * Az eladó (a mi cégünk) adatai a Billingo-fiókból - CSAK az előnézet eladó-blokkjához.
 *
 * Csak olvas: a szerver egy már kiállított dokumentum `organization` blokkjából dolgozik
 * (a `GET /organization` nem adja meg a nevet és a címet), és a konfigurált bankszámlát
 * teszi mellé. `null`, ha még nincs miből - a hívó ilyenkor helyőrzőt mutat. A hibát NEM
 * nyeljük el itt: azt a hook teszi csendben (`useInvoiceIssuer`), mert a hiányzó eladó-adat
 * az előnézet dolga, nem hiba - de egy másik hívó még dönthet másképp.
 *
 * Ami nem egy névvel bíró eladó, az `null`: egy félresikerült válasz se jusson a papírra.
 */
export const getInvoiceIssuer = async (): Promise<InvoiceIssuer | null> => {
  const getInvoiceIssuerFn = httpsCallable<Record<string, never>, { issuer?: InvoiceIssuer | null }>(
    functions,
    "getInvoiceIssuer",
    { timeout: ISSUER_TIMEOUT_MS },
  );
  const result = await getInvoiceIssuerFn({});
  const issuer = result.data?.issuer;
  return issuer && typeof issuer.name === "string" && issuer.name.trim() ? issuer : null;
};
